import { formatRupees } from '@quickreport/domain';
import {
  deletePatient,
  type PatientDeletionImpact,
  patientDeletionImpact,
} from '@quickreport/workspace';
import { useEffect, useState } from 'react';
import { Consequence, DeleteConfirm } from '../../components/DeleteConfirm';
import { useWorkspace } from '../../lib/workspace';
import { useCtx } from '../reports/context';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Ask before deleting a patient together with everything under them. */
export function DeletePatientDialog({
  patientId,
  onDeleted,
  onClose,
}: {
  patientId: string;
  onDeleted: () => void;
  onClose: () => void;
}) {
  const ws = useWorkspace();
  const ctx = useCtx();
  const [impact, setImpact] = useState<PatientDeletionImpact | null>(null);

  useEffect(() => {
    void patientDeletionImpact(ws.db, ctx.roles, patientId).then(setImpact);
  }, [ws.db, ctx.roles, patientId]);

  if (!impact) return null;
  const nothing = impact.reports === 0;

  return (
    <DeleteConfirm
      title="Delete this patient?"
      subject={
        <>
          <div className="font-semibold">{impact.name}</div>
          <div className="mt-0.5 text-slate">
            {nothing
              ? 'No reports yet'
              : `${plural(impact.reports, 'report')} in ${plural(impact.visits, 'visit')}`}
          </div>
        </>
      }
      consequences={
        <>
          <Consequence>
            {impact.name} is removed from your patient list and from search, so the front desk can
            no longer pick them.
          </Consequence>
          {nothing ? null : (
            <Consequence tone="warn">
              <b>All {plural(impact.reports, 'report')}</b> of this patient are deleted too
              {impact.signed > 0
                ? `, including ${impact.signed} that ${impact.signed === 1 ? 'is' : 'are'} already signed`
                : ''}
              .
            </Consequence>
          )}
          {impact.hasLink ? (
            <Consequence tone="warn">
              The QR codes and links on copies the patient has will stop working. Their “my reports”
              page will be empty.
            </Consequence>
          ) : null}
          {impact.payments > 0 ? (
            <Consequence tone="warn">
              {formatRupees(impact.paidPaise)} received in {plural(impact.payments, 'payment')} is
              removed from your payments and income too.
            </Consequence>
          ) : nothing ? null : (
            <Consequence>No payments were recorded, so no money changes.</Consequence>
          )}
          <Consequence>Other patients, even on the same phone number, are not touched.</Consequence>
        </>
      }
      note={
        <>
          Only want to remove a mistake? Open the report and delete just that one. This cannot be
          undone from the app. A note of who deleted this patient is kept in the history.
          {impact.signed > 0
            ? ' Signed reports still count on your LabView bill if you pay per report.'
            : ''}
        </>
      }
      confirmLabel="Delete patient"
      typeToConfirm={!nothing}
      blockedReason={impact.blockedReason}
      onClose={onClose}
      onConfirm={async () => {
        await deletePatient(ws.db, ctx, patientId);
        void ws.engine.refreshCounts();
        void ws.engine.syncNow();
        onDeleted();
      }}
    />
  );
}
